import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert, Grid, Button, Chip, Divider } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import EventIcon from '@mui/icons-material/Event';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

function ItemDetailsPage() {
    const { itemId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchItem = async () => {
            try {
                const { data } = await axios.get(`/api/items/${itemId}`);
                setItem(data);
            } catch (err) {
                setError('Failed to load item details. It may have been deleted.');
            } finally {
                setLoading(false);
            }
        };
        fetchItem();
    }, [itemId]);

    const handleStartChat = async (item) => {
        if (!token) {
            alert("Please login to contact the person.");
            navigate('/login');
            return;
        }
        try {
            const recipientId = item.type === 'found' ? item.foundBy : item.reportedBy;
            const { data: conversation } = await axios.post('/api/messaging/start', 
                { itemId: item._id, recipientId: recipientId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            navigate(`/chat/${conversation._id}`);
        } catch (err) {
            setError(err.response?.data?.message || "Could not start conversation.");
        }
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!item) return <Typography>Item not found.</Typography>;

    return (
        <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 4 }}>
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Box component="img"
                        className="item-detail-image"
                        src={item.image || 'https://via.placeholder.com/600x400?text=No+Image'}
                        alt={item.itemName}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Chip 
                        label={item.type === 'found' ? 'Found Item' : 'Lost Item'} 
                        color={item.type === 'found' ? 'success' : 'warning'} 
                        sx={{ mb: 2, fontWeight: 'bold' }} 
                    />
                    <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                        {item.itemName}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        {item.description}
                    </Typography>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ my: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <CategoryIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                            <Typography><strong>Category:</strong> {item.category}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <LocationOnIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                            <Typography><strong>Location:</strong> {item.district}, {item.state}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <EventIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                            <Typography><strong>Date Posted:</strong> {new Date(item.date).toLocaleDateString()}</Typography>
                        </Box>
                    </Box>
                    
                    {item.status === 'resolved' ? (
                        <Alert severity="success">This item has been marked as resolved.</Alert>
                    ) : (
                        <Button variant="contained" size="large" onClick={() => handleStartChat(item)}>
                            Contact Person
                        </Button>
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
}

export default ItemDetailsPage;
